import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSupabaseClient, useSession } from '@supabase/auth-helpers-react';
import { AuthError } from '@supabase/supabase-js';
import AuthLayout from '../../components/AuthLayout';
import { PASSWORD_HELP_TEXT, validateNewPassword } from '../../utils/passwordValidation';
import {
  SignupFormData,
  SignupState,
  DEFAULT_SIGNUP_FORM_DATA,
  DEFAULT_SIGNUP_STATE,
  VALIDATION_RULES,
  FIELD_CONSTRAINTS,
  TEAM_MEMBER_TYPE_OPTIONS,
  AUTH_ROUTES,
  SUCCESS_MESSAGES,
  ERROR_MESSAGES
} from './config';

const Signup: React.FC = () => {
  const [formData, setFormData] = useState<SignupFormData>(DEFAULT_SIGNUP_FORM_DATA);
  const [signupState, setSignupState] = useState<SignupState>({ ...DEFAULT_SIGNUP_STATE, step: 'form' });
  
  const supabase = useSupabaseClient();
  const session = useSession();
  const navigate = useNavigate();

  // Check if user is authenticated (they should be since this is a ProtectedRoute)
  useEffect(() => {
    if (!session) {
      navigate(AUTH_ROUTES.LOGIN);
    }
  }, [session, navigate]);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (signupState.error) {
      setSignupState(prev => ({
        ...prev,
        error: undefined
      }));
    }
  };

  // Validate form data
  const validateForm = (): string | null => {
    const { firstName, lastName, type, password, confirmPassword } = formData;

    // First name validation
    if (!firstName.trim()) {
      return VALIDATION_RULES.FIRST_NAME.REQUIRED;
    }
    if (firstName.length < FIELD_CONSTRAINTS.FIRST_NAME.MIN_LENGTH) {
      return VALIDATION_RULES.FIRST_NAME.MIN_LENGTH;
    }
    if (firstName.length > FIELD_CONSTRAINTS.FIRST_NAME.MAX_LENGTH) {
      return VALIDATION_RULES.FIRST_NAME.MAX_LENGTH;
    }

    // Last name validation
    if (!lastName.trim()) {
      return VALIDATION_RULES.LAST_NAME.REQUIRED;
    }
    if (lastName.length < FIELD_CONSTRAINTS.LAST_NAME.MIN_LENGTH) {
      return VALIDATION_RULES.LAST_NAME.MIN_LENGTH;
    }
    if (lastName.length > FIELD_CONSTRAINTS.LAST_NAME.MAX_LENGTH) {
      return VALIDATION_RULES.LAST_NAME.MAX_LENGTH;
    }

    // Type validation
    if (!type) {
      return VALIDATION_RULES.TYPE.REQUIRED;
    }

    // Shared password policy — same checks as ResetPassword/ChangePasswordModal
    return validateNewPassword(password, confirmPassword);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setSignupState(prev => ({
        ...prev,
        error: validationError
      }));
      return;
    }

    setSignupState({
      step: 'processing',
      isLoading: true
    });

    try {
      // Update user password (invited users need to set their password)
      const { error: userError } = await supabase.auth.updateUser({
        password: formData.password
      });

      if (userError) {
        throw userError;
      }

      // Create the team_members + profiles pair atomically. The RPC is
      // idempotent, so a retry after a partial failure is safe.
      const { error: signupError } = await supabase.rpc('complete_signup', {
        p_first_name: formData.firstName.trim(),
        p_last_name: formData.lastName.trim(),
        p_member_type: formData.type
      });

      if (signupError) {
        throw signupError;
      }

      // Success - show completion message
      setSignupState({
        step: 'complete',
        isLoading: false
      });

      // Redirect to timesheets after a short delay
      setTimeout(() => {
        navigate(AUTH_ROUTES.TIMESHEETS);
      }, 2000);

    } catch (error) {
      console.error('Signup error:', error);
      const authError = error as AuthError;
      
      let errorMessage: string = ERROR_MESSAGES.GENERIC_ERROR;
      if (authError.message?.includes('password')) {
        errorMessage = ERROR_MESSAGES.SIGNUP_FAILED;
      } else if (
        authError.message?.includes('complete_signup') ||
        authError.message?.includes('invalid_name') ||
        authError.message?.includes('not_authenticated')
      ) {
        errorMessage = ERROR_MESSAGES.PROFILE_CREATION_FAILED;
      }

      setSignupState({
        step: 'error',
        error: errorMessage,
        isLoading: false
      });
    }
  };

  // Render completion state
  if (signupState.step === 'complete') {
    return (
      <AuthLayout>
        <div className="auth-card__status">
          <div className="auth-card__status-icon">✓</div>
          <h2>{SUCCESS_MESSAGES.ACCOUNT_CREATED}</h2>
          <p>{SUCCESS_MESSAGES.PROFILE_COMPLETE}</p>
          <p>{SUCCESS_MESSAGES.REDIRECT_MESSAGE}</p>
        </div>
      </AuthLayout>
    );
  }

  // Render main signup form
  return (
    <AuthLayout>
      <h1 className="auth-card__title">Complete your account</h1>
      <p className="auth-card__subtitle">
        Set your password and profile details to access the system.
      </p>

      <form onSubmit={handleSubmit} className="auth-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="firstName">First name</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              placeholder="First name"
              disabled={signupState.isLoading}
              autoComplete="given-name"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="lastName">Last name</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              placeholder="Last name"
              disabled={signupState.isLoading}
              autoComplete="family-name"
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="type">Your role</label>
          <select
            id="type"
            name="type"
            value={formData.type}
            onChange={handleInputChange}
            disabled={signupState.isLoading}
            required
          >
            {TEAM_MEMBER_TYPE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="password">Password</label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            placeholder="Create a password"
            disabled={signupState.isLoading}
            autoComplete="new-password"
            required
          />
          <div className="password-requirements">
            <small>{PASSWORD_HELP_TEXT}</small>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm password</label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            placeholder="Repeat your password"
            disabled={signupState.isLoading}
            autoComplete="new-password"
            required
          />
        </div>

        {signupState.error && (
          <div className="error-message">
            {signupState.error}
          </div>
        )}

        <button
          type="submit"
          className="auth-submit"
          disabled={signupState.isLoading}
        >
          {signupState.isLoading ? 'Setting up account...' : 'Complete account setup'}
        </button>
      </form>

      <div className="auth-card__footer">
        Already have an account?{' '}
        <button
          type="button"
          className="link-button"
          onClick={() => navigate(AUTH_ROUTES.LOGIN)}
          disabled={signupState.isLoading}
        >
          Sign in here
        </button>
      </div>
    </AuthLayout>
  );
};

export default Signup;
